import { useMemo, useRef, useCallback, useEffect } from 'react';
import { WIND_SECTORS } from '../lib/courseConstants.js';
import { canonicalizeMarkIds } from '../lib/coursePreviewLayout.js';
import { buildMarkLookup, calcCourseDistance, isCourseInvalid } from './CardGrid.jsx';
import './NamedCourseGrid.css';

const SECTOR_CODES = new Set(WIND_SECTORS.map((s) => s.code));

/**
 * Partition a card's named courses into layout rows for keyboard navigation.
 *
 * Two layouts share one arrow-key sequence:
 *   - Courses WITH a wind sector lay out as a grid — the 8 WIND_SECTORS as fixed
 *     rows (mirroring CardGrid), each holding however many courses carry that
 *     sector (ragged). The sector grid only appears when at least one course is
 *     assigned.
 *   - Courses with NO/unknown sector (windDirection == null, the newNamedCourse
 *     default) render as a simple vertical LIST — one course per row. When every
 *     course is unassigned there is no grid at all, just the list. (ra-dzqv CR2)
 *
 * Every returned row exposes a `courses` array so the shared nav logic (skip
 * empty rows, clamp column) treats grid rows and single-item list rows the same.
 *
 * @param {Array} courses - Named courses (directionCode == null)
 * @returns {Array<{ kind: 'grid'|'list', key: string, code?: string,
 *   label?: string, courses: Array }>}
 */
export function buildLayoutRows(courses) {
  const buckets = new Map(WIND_SECTORS.map((s) => [s.code, []]));
  const unassigned = [];
  for (const c of courses || []) {
    if (SECTOR_CODES.has(c.windDirection)) buckets.get(c.windDirection).push(c);
    else unassigned.push(c);
  }

  const hasAssigned = WIND_SECTORS.some((s) => buckets.get(s.code).length > 0);

  const rows = [];
  if (hasAssigned) {
    for (const s of WIND_SECTORS) {
      rows.push({ kind: 'grid', key: `sector:${s.code}`, code: s.code, label: s.label, courses: buckets.get(s.code) });
    }
  }
  // Each unassigned course is its own single-cell list row.
  for (const c of unassigned) {
    rows.push({ kind: 'list', key: `list:${c.id}`, courses: [c] });
  }
  return rows;
}

/**
 * Named-course navigator for a card. Assigned courses render as a wind-sector
 * grid mirroring CardGrid; unassigned courses render as a simple list. Both are
 * driven by one roving-tabindex arrow-key sequence (Up/Down between rows,
 * Left/Right within a grid row; Tab falls through). (ra-dzqv)
 *
 * @param {Object} props
 * @param {Array} props.courses - Named courses for the current card
 * @param {Array} props.marks - Club marks with coordinates
 * @param {string | null} props.selectedCourseId - Currently selected course id
 * @param {(id: string) => void} props.onSelectCourse - Selection callback
 */
export default function NamedCourseGrid({ courses, marks, selectedCourseId, onSelectCourse }) {
  const gridRef = useRef(null);
  const markLookup = useMemo(() => buildMarkLookup(marks || []), [marks]);

  const rows = useMemo(() => buildLayoutRows(courses), [courses]);
  const gridRows = useMemo(() => rows.filter((r) => r.kind === 'grid'), [rows]);
  const listRows = useMemo(() => rows.filter((r) => r.kind === 'list'), [rows]);

  // First row that actually holds a course — the default keyboard entry point
  // and roving-tabindex anchor when nothing is selected.
  const firstPopulatedRow = useMemo(
    () => rows.findIndex((r) => r.courses.length > 0),
    [rows],
  );

  // Widest sector fixes the grid column count so every grid row lays out on the
  // same CSS tracks (no flex wrap). Short rows pad with filler cells; the wrapper
  // scrolls horizontally on overflow. List rows don't affect this. (ra-dzqv CR)
  const maxCols = useMemo(
    () => Math.max(1, ...gridRows.map((r) => r.courses.length)),
    [gridRows],
  );

  // Locate the selected course in (row, col) index space.
  const findPosition = useCallback(
    (courseId) => {
      for (let r = 0; r < rows.length; r++) {
        const c = rows[r].courses.findIndex((crs) => crs.id === courseId);
        if (c >= 0) return { row: r, col: c };
      }
      return null;
    },
    [rows],
  );

  // Resolve the selected id against THIS card's courses. A selected id from a
  // previously-viewed card (the parent clears selectedCell but may leave a stale
  // selectedNamedCourseId on a card switch) resolves to null here — treated as
  // "no selection" so the default entry cell stays tabbable and the keyboard can
  // still reach the widget. (codex on d86dc8f)
  const selectedPos = useMemo(
    () => (selectedCourseId ? findPosition(selectedCourseId) : null),
    [selectedCourseId, findPosition],
  );

  // The cell that is tabbable when the selection doesn't resolve here.
  const defaultEntryId =
    !selectedPos && firstPopulatedRow >= 0 ? rows[firstPopulatedRow].courses[0].id : null;

  // Arrow-key navigation mirroring CardGrid.handleKeyDown, adapted for ragged
  // rows + single-cell list rows: Up/Down skip empty rows and clamp the column
  // into the target row; Left/Right move within a row (a no-op on list rows).
  const handleKeyDown = useCallback(
    (e) => {
      const navKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!navKeys.includes(e.key)) return; // let Tab etc. fall through

      if (!selectedPos) {
        // Nothing selected (or a stale id from another card) — enter at the first
        // populated cell.
        if (firstPopulatedRow >= 0) {
          e.preventDefault();
          onSelectCourse(rows[firstPopulatedRow].courses[0].id);
        }
        return;
      }

      let { row, col } = selectedPos;
      switch (e.key) {
        case 'ArrowLeft':
          col = Math.max(0, col - 1);
          break;
        case 'ArrowRight':
          col = Math.min(rows[row].courses.length - 1, col + 1);
          break;
        case 'ArrowUp': {
          let r = row - 1;
          while (r >= 0 && rows[r].courses.length === 0) r--;
          if (r < 0) return; // no populated row above
          row = r;
          col = Math.min(col, rows[row].courses.length - 1);
          break;
        }
        case 'ArrowDown': {
          let r = row + 1;
          while (r < rows.length && rows[r].courses.length === 0) r++;
          if (r >= rows.length) return; // no populated row below
          row = r;
          col = Math.min(col, rows[row].courses.length - 1);
          break;
        }
        default:
          return;
      }

      e.preventDefault();
      onSelectCourse(rows[row].courses[col].id);
    },
    [selectedPos, firstPopulatedRow, rows, onSelectCourse],
  );

  // Focus the selected cell when selection changes (mirrors CardGrid).
  useEffect(() => {
    if (!selectedCourseId || !gridRef.current) return;
    const cell = gridRef.current.querySelector(`[data-course-id="${selectedCourseId}"]`);
    if (cell) cell.focus();
  }, [selectedCourseId]);

  // Shared per-course derived data for both layouts.
  const cellInfo = useCallback(
    (course) => {
      const canonMarkIds = canonicalizeMarkIds(course.markIds, marks);
      const markCount = canonMarkIds ? canonMarkIds.length : 0;
      const dist = calcCourseDistance({ markIds: canonMarkIds }, markLookup);
      const invalid = isCourseInvalid({ markIds: canonMarkIds }, markLookup);
      const name = course.name || 'Unnamed';
      const distLabel = dist > 0 ? `${dist.toFixed(1)} nm` : 'no distance';
      return { markCount, dist, invalid, name, distLabel };
    },
    [marks, markLookup],
  );

  return (
    <div className="named-grid-wrap">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="named-grid-widget"
        role="grid"
        aria-label="Named courses"
        ref={gridRef}
        onKeyDown={handleKeyDown}
      >
        {gridRows.length > 0 && (
          <div
            className="named-grid"
            role="rowgroup"
            // A real CSS grid keyed off the widest sector so rows share columns
            // and never wrap; the wrapper scrolls when they overflow. (ra-dzqv CR)
            style={{ gridTemplateColumns: `auto repeat(${maxCols}, minmax(68px, 132px))` }}
          >
            {gridRows.map((row) => (
              <div className="named-grid-row" role="row" key={row.key}>
                <div className="named-grid-row-label" role="rowheader" title={row.label}>
                  {row.code}
                </div>
                {Array.from({ length: maxCols }, (_, colIdx) => {
                  const course = row.courses[colIdx];
                  if (!course) {
                    // Empty slot padding a short row to the column count.
                    return <div key={colIdx} className="named-grid-cell--filler" aria-hidden="true" />;
                  }
                  const isSelected = course.id === selectedCourseId;
                  const tabIndex = isSelected || course.id === defaultEntryId ? 0 : -1;
                  const { markCount, dist, invalid, name, distLabel } = cellInfo(course);
                  const classes = [
                    'named-grid-cell',
                    isSelected ? 'named-grid-cell--selected' : '',
                    invalid ? 'named-grid-cell--invalid' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <button
                      key={course.id}
                      className={classes}
                      onClick={() => onSelectCourse(course.id)}
                      title={`${name} — ${markCount} marks — ${distLabel}`}
                      aria-label={`Named course ${name}, ${markCount} marks, ${distLabel}`}
                      aria-pressed={isSelected || undefined}
                      tabIndex={tabIndex}
                      data-course-id={course.id}
                    >
                      <span className="named-grid-cell-name">{name}</span>
                      {/* Metadata stacked vertically so tiles stay narrow. */}
                      <span className="named-grid-cell-meta">{markCount} marks</span>
                      {dist > 0 && (
                        <span className="named-grid-cell-meta">{dist.toFixed(1)} nm</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {listRows.length > 0 && (
          <div className="named-list" role="rowgroup">
            {gridRows.length > 0 && (
              <div className="named-list-header" aria-hidden="true">
                Unassigned
              </div>
            )}
            {listRows.map((row) => {
              const course = row.courses[0];
              const isSelected = course.id === selectedCourseId;
              const tabIndex = isSelected || course.id === defaultEntryId ? 0 : -1;
              const { markCount, dist, invalid, name, distLabel } = cellInfo(course);
              const classes = [
                'named-list-item',
                isSelected ? 'named-list-item--selected' : '',
                invalid ? 'named-list-item--invalid' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <div className="named-list-row" role="row" key={row.key}>
                  <button
                    className={classes}
                    onClick={() => onSelectCourse(course.id)}
                    title={`${name} — ${markCount} marks — ${distLabel}`}
                    aria-label={`Named course ${name}, ${markCount} marks, ${distLabel}`}
                    aria-pressed={isSelected || undefined}
                    tabIndex={tabIndex}
                    data-course-id={course.id}
                  >
                    <span className="named-list-item-name">{name}</span>
                    <span className="named-list-item-meta">
                      {markCount} marks
                      {dist > 0 && <> &middot; {dist.toFixed(1)} nm</>}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
